import { useState } from "react";
import { Link } from "react-router-dom";
import { BrowserProvider, Contract, ContractFactory, JsonRpcProvider } from "ethers";
import { toast } from "sonner";
import { Globe, Loader2, ExternalLink, Droplets, CheckCircle2 } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { NETWORK_CONFIG } from "@/constants/network";
import { EXTERNAL_CHAINS, addLiquidityUrl } from "@/constants/externalChains";
import { Button } from "@/components/ui/button";

const storeKey = (token) => `fsc-multichain-${token.address.toLowerCase()}`;
const loadDeployments = (token) => { try { return JSON.parse(localStorage.getItem(storeKey(token))) || {}; } catch { return {}; } };

// Starter tokens take a whole-number supply and mint it with 18 decimals
const fscSupply = async (address) => {
  const fsc = new JsonRpcProvider(NETWORK_CONFIG.rpcUrls[0]);
  const raw = await new Contract(address, ["function totalSupply() view returns (uint256)"], fsc).totalSupply();
  return raw / 10n ** 18n;
};

const switchChain = async (eip1193, chain) => {
  const chainId = `0x${chain.id.toString(16)}`;
  try {
    await eip1193.request({ method: "wallet_switchEthereumChain", params: [{ chainId }] });
  } catch (err) {
    if (err?.code !== 4902 && !/unrecognized|not added|unknown/i.test(err?.message || "")) throw err;
    await eip1193.request({ method: "wallet_addEthereumChain", params: [{ chainId, chainName: chain.name,
      nativeCurrency: { name: chain.coin, symbol: chain.coin, decimals: 18 }, rpcUrls: [chain.rpc], blockExplorerUrls: [chain.explorer] }] });
  }
};

const failMessage = (err, chain) => {
  if (err?.code === "ACTION_REJECTED" || err?.code === 4001) return "Rejected in your wallet";
  if (/insufficient funds/i.test(err?.message || "")) return `Not enough ${chain.coin} for gas on ${chain.name}`;
  return (err?.shortMessage || err?.message || "Deployment failed").slice(0, 140);
};

const ChainRow = ({ chain, deployed, busy, onDeploy }) => {
  const lp = deployed && addLiquidityUrl(chain, deployed);
  return (
    <div data-testid={`chain-row-${chain.id}`} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-[#050505] border border-[#2A2411]">
      <div className="text-sm">
        <span className="text-white font-medium">{chain.name}</span>
        <span className="text-zinc-500 text-xs ml-2">gas in {chain.coin}{chain.testnet ? " · testnet" : ""}</span>
      </div>
      {deployed ? (
        <div className="flex items-center gap-3 text-xs">
          <a data-testid={`chain-explorer-${chain.id}`} href={`${chain.explorer}/token/${deployed}`} target="_blank" rel="noreferrer" className="text-green-400 inline-flex items-center gap-1">
            <CheckCircle2 size={12} /> {deployed.slice(0, 6)}…{deployed.slice(-4)} <ExternalLink size={10} />
          </a>
          {lp && <a data-testid={`chain-liquidity-${chain.id}`} href={lp} target="_blank" rel="noreferrer" className="text-[#D4AF37] inline-flex items-center gap-1"><Droplets size={12} /> Add liquidity</a>}
        </div>
      ) : (
        <Button data-testid={`chain-deploy-${chain.id}`} size="sm" variant="outline" disabled={busy} onClick={() => onDeploy(chain)}
          className="h-8 border-[#D4AF37]/30 text-[#D4AF37] bg-transparent hover:bg-[#D4AF37]/10 hover:text-[#F3E5AB]">
          {busy === chain.id ? <Loader2 size={12} className="animate-spin" /> : "Deploy"}
        </Button>
      )}
    </div>
  );
};

export const MultiChainDeploy = ({ token }) => {
  const { getProvider, addFSCNetwork } = useWallet();
  const [deployments, setDeployments] = useState(() => loadDeployments(token));
  const [busy, setBusy] = useState(null);

  const deploy = async (chain) => {
    const eip1193 = getProvider();
    if (!eip1193) return toast.error("Connect your wallet first");
    setBusy(chain.id);
    try {
      const artifact = await (await fetch("/contracts/StarterToken.json")).json();
      const supply = token.supply ? BigInt(token.supply) : await fscSupply(token.address);
      await switchChain(eip1193, chain);
      const signer = await new BrowserProvider(eip1193).getSigner();
      const contract = await new ContractFactory(artifact.abi, artifact.bytecode, signer).deploy(token.name, token.symbol, supply);
      toast.info(`Deploying ${token.symbol} on ${chain.name}…`);
      await contract.waitForDeployment();
      const next = { ...deployments, [chain.id]: await contract.getAddress() };
      localStorage.setItem(storeKey(token), JSON.stringify(next));
      setDeployments(next);
      toast.success(`${token.symbol} is live on ${chain.name}`);
    } catch (err) {
      toast.error(failMessage(err, chain));
    } finally {
      setBusy(null);
      addFSCNetwork().catch(() => {});
    }
  };

  return (
    <div data-testid="multichain-deploy" className="space-y-3 pt-2">
      <div className="flex items-center gap-2"><Globe size={15} className="text-[#D4AF37]" /><h3 className="text-sm font-semibold text-white">Add liquidity & launch on more networks</h3></div>
      <Link data-testid="starter-fsc-liquidity-link" to={`/dex?token=${token.address}`}
        className="flex items-center justify-between p-3 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/15">
        <span className="inline-flex items-center gap-2"><Droplets size={14} /> Add {token.symbol} liquidity on {NETWORK_CONFIG.chainName} DEX</span><ExternalLink size={12} />
      </Link>
      <p className="text-xs text-zinc-500">Deploy the same {token.symbol} contract on other blockchains from your wallet. You pay real gas on each chain, and each copy is a separate token. Logos on other chains are set through their explorers and wallets, not here.</p>
      <div className="space-y-2">
        {EXTERNAL_CHAINS.map((c) => <ChainRow key={c.id} chain={c} deployed={deployments[c.id]} busy={busy} onDeploy={deploy} />)}
      </div>
    </div>
  );
};
